import { audioCtx } from '../audio/context';
import { registerBuffer } from '../audio/decode';
import type { SourceMeta } from '../types';

/**
 * Sample packs — real audio dropped into `public/breaks/`.
 *
 * Everything in the built-in library is synthesised, which sidesteps licensing
 * entirely. This is the escape hatch for audio you actually have the right to
 * ship: drop the files in, add a line to the manifest, and they appear in the
 * library next to the generated kits.
 *
 * The manifest carries `licence` and `credit` because several of the licences
 * that permit redistribution (CC-BY in particular) require attribution to
 * travel with the file. Those fields are surfaced in the UI rather than kept in
 * a README nobody reads.
 */

export interface PackBreak {
  id: string;
  name: string;
  /** Filename inside `public/breaks/`. */
  file: string;
  /** Known tempo. Omit and Happy Chopper detects it on load. */
  bpm?: number;
  /** Known length in bars. Omit and it's inferred from bpm and duration. */
  bars?: number;
  description?: string;
  /** e.g. "CC0", "CC BY 4.0", "Public domain". */
  licence?: string;
  /** Attribution line, shown in the library. */
  credit?: string;
  /** Where it came from. */
  source?: string;
}

interface Manifest {
  breaks?: PackBreak[];
}

const MANIFEST_URL = '/breaks/manifest.json';

/**
 * Reads the pack manifest. A missing or malformed manifest is not an error —
 * the app ships without one and falls back to the synthesised kits.
 */
export async function loadBreakPack(): Promise<PackBreak[]> {
  try {
    const res = await fetch(MANIFEST_URL, { cache: 'no-cache' });
    if (!res.ok) return [];
    const data = (await res.json()) as Manifest;
    if (!Array.isArray(data.breaks)) return [];
    return data.breaks.filter((b) => b && b.id && b.name && b.file);
  } catch {
    return [];
  }
}

/** Fetches, decodes and registers one pack break. */
export async function importPackBreak(
  entry: PackBreak,
  beatsPerBar: number,
): Promise<SourceMeta> {
  const res = await fetch(`/breaks/${entry.file}`);
  if (!res.ok) throw new Error(`Couldn't load ${entry.file} (${res.status})`);
  const buffer = await audioCtx().decodeAudioData(await res.arrayBuffer());

  // A declared tempo is trusted over detection — whoever wrote the manifest
  // knows more than the analyser does.
  const { meta } = registerBuffer(
    buffer,
    entry.name,
    beatsPerBar,
    entry.bpm ? { bpm: entry.bpm } : {},
  );
  if (entry.bars) meta.bars = entry.bars;
  return meta;
}
